import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import HitlDetailDrawer from "@/components/hitl/HitlDetailDrawer";
import HitlActionPrompt from "@/components/hitl/HitlActionPrompt";
import HitlBadge from "@/components/hitl/HitlBadge";
import LockedHitlDrawer from "@/components/hitl/LockedHitlDrawer";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { useHitlInbox } from "@/hooks/useHitlInbox";
import { useAgentTaskInbox, useAgentTaskActions, getTaskStatusColor, getTaskStatusLabel, getTaskTypeLabel } from "@/hooks/useAgentTasksHitl";
import { useHasCapability } from "@/hooks/useCapabilities";
import { useUpgradeModal } from "@/hooks/useUpgradeModal";
import { HITL_CATEGORIES, type HitlInboxFilters, type HitlItem } from "@/types/hitl";
import { cn } from "@/lib/utils";
import { listThreadMessages } from "@/api/email";
import { approveHitlItem } from "@/api/hitl";
import { fetchContacts } from "@/api/crm";
import { useToast } from "@/components/ToastProvider";
import type { CrmContact } from "@/types/crm";

type ContactDraft = {
  name: string;
  email: string;
  city: string;
  state: string;
};

function formatDate(value?: string | null) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
}

export default function HitlInbox() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialCategory = (searchParams.get("category") as HitlInboxFilters["category"]) ?? "all";
  const initialMineOnly = searchParams.get("mine") === "1";
  const selectedId = searchParams.get("open");
  const threadParam = searchParams.get("thread_id");
  const viewMode = searchParams.get("view") ?? "workflow"; // "workflow" or "agent_tasks"

  const { items, loading, error, filters, setFilters, refresh, approve, reject } = useHitlInbox({
    filters: { category: initialCategory, mineOnly: initialMineOnly, status: "pending" },
    toastOnUpdate: false,
  });

  // Agent tasks inbox
  const { pendingTasks, isLoading: tasksLoading, error: tasksError, mutate: refreshTasks } = useAgentTaskInbox();
  const { approveTask, rejectTask, isSubmitting } = useAgentTaskActions();

  const [rejectPrompt, setRejectPrompt] = useState<{ id: string } | null>(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [threadMessages, setThreadMessages] = useState<Record<string, any[]>>({});
  const [threadLoading, setThreadLoading] = useState<Record<string, boolean>>({});
  const [contactEdits, setContactEdits] = useState<Record<string, ContactDraft>>({});
  const [threadWorkspace, setThreadWorkspace] = useState<{ threadId: string; itemId: string } | null>(null);
  const [contactSearchOpen, setContactSearchOpen] = useState<Record<string, boolean>>({});
  const [contactSearchQuery, setContactSearchQuery] = useState<Record<string, string>>({});
  const [contactSearchResults, setContactSearchResults] = useState<Record<string, CrmContact[]>>({});
  const [contactSearchLoading, setContactSearchLoading] = useState<Record<string, boolean>>({});
  const [contactSearchError, setContactSearchError] = useState<Record<string, string>>({});
  const { push } = useToast();
  const { openUpgradeModal } = useUpgradeModal();
  const canOpenDrawer = useHasCapability("canOpenHitlDrawer");
  const canApprove = useHasCapability("canApproveHitl");
  const canReject = useHasCapability("canRejectHitl");
  const canResolve = useHasCapability("canResolveHitlItem");
  const canApproveAgentTasks = useHasCapability("canApproveAgentTasks");
  const canRejectAgentTasks = useHasCapability("canRejectAgentTasks");

  const buildParams = (nextFilters: HitlInboxFilters, opts: { openId?: string | null; threadId?: string | null } = {}) => {
    const params = new URLSearchParams();
    if (nextFilters.category && nextFilters.category !== "all") params.set("category", nextFilters.category);
    if (nextFilters.mineOnly) params.set("mine", "1");
    if (opts.openId) params.set("open", opts.openId);
    if (opts.threadId) params.set("thread_id", opts.threadId);
    return params;
  };

  const handleCategoryChange = (categoryId: HitlInboxFilters["category"]) => {
    if (categoryId === filters.category) return;
    const next = { ...filters, category: categoryId };
    setFilters(next);
    setSearchParams(buildParams(next, { openId: selectedId, threadId: threadParam ?? undefined }), { replace: true });
  };

  const handleMineToggle = (checked: boolean) => {
    if (Boolean(filters.mineOnly) === checked) return;
    const next = { ...filters, mineOnly: checked };
    setFilters(next);
    setSearchParams(buildParams(next, { openId: selectedId, threadId: threadParam ?? undefined }), { replace: true });
  };

  const groupedItems = (items ?? []).filter((item) => item.status === "pending");

  const dedupedItems = (() => {
    const seen = new Map<string, HitlItem>();
    const remainder: HitlItem[] = [];
    groupedItems.forEach((item) => {
      const payload = (item.payload ?? {}) as Record<string, any>;
      const fromAddress = (payload.from_address || "").trim().toLowerCase();
      const isCrmEmail = item.category === "crm" && fromAddress;
      if (!isCrmEmail) {
        remainder.push(item);
        return;
      }
      const prev = seen.get(fromAddress);
      if (!prev) {
        seen.set(fromAddress, item);
        return;
      }
      const prevTime = prev.created_at ? Date.parse(prev.created_at) : 0;
      const nextTime = item.created_at ? Date.parse(item.created_at) : 0;
      if (nextTime > prevTime) {
        seen.set(fromAddress, item);
      }
    });
    return [...seen.values(), ...remainder];
  })();

  const openInApprovals = (item: HitlItem) => {
    const params = new URLSearchParams();
    const category = item.category ?? filters.category ?? "all";
    if (category && category !== "all") params.set("category", String(category));
    params.set("open", item.id);
    if (item.thread_id) params.set("thread_id", item.thread_id);
    if (filters.mineOnly) params.set("mine", "1");
    const query = params.toString();
    navigate(query ? `/agents/observability?${query}` : "/agents/observability");
  };

  useEffect(() => {
    const categoryParam = (searchParams.get("category") as HitlInboxFilters["category"]) ?? "all";
    const mineParam = searchParams.get("mine") === "1";
    setFilters((prev) => {
      let changed = false;
      const next: HitlInboxFilters = { ...prev };
      if (categoryParam !== prev.category) {
        next.category = categoryParam;
        changed = true;
      }
      if (mineParam !== Boolean(prev.mineOnly)) {
        next.mineOnly = mineParam;
        changed = true;
      }
      return changed ? next : prev;
    });
  }, [searchParams, setFilters]);

  useEffect(() => {
    if (!threadParam || selectedId) return;
    const match = groupedItems.find((entry) => entry.thread_id === threadParam);
    if (match) {
      setSearchParams(buildParams(filters, { openId: match.id, threadId: threadParam }), { replace: true });
    }
  }, [filters, groupedItems, selectedId, setSearchParams, threadParam]);

  const openDrawer = (itemId: string, threadId?: string | null) => {
    if (!canOpenDrawer) {
      openUpgradeModal("Open HITL drawer");
      return;
    }
    setSearchParams(buildParams(filters, { openId: itemId, threadId: threadId ?? threadParam ?? undefined }), { replace: true });
  };

  const closeDrawer = () => {
    setSearchParams(buildParams(filters, { threadId: threadParam ?? undefined }), { replace: true });
  };

  const handleRejectPrompt = async () => {
    if (!rejectPrompt) return;
    if (!canReject) {
      openUpgradeModal("Reject HITL");
      return;
    }
    try {
      await reject(rejectPrompt.id, rejectNotes.trim());
      setRejectPrompt(null);
      setRejectNotes("");
    } catch (error: any) {
      if (error?.error === "requires_pro") {
        openUpgradeModal("Reject HITL");
      }
    }
  };

  const handleApprove = async (itemId: string, body?: Record<string, unknown>) => {
    if (!canApprove) {
      openUpgradeModal("Approve HITL");
      return;
    }
    try {
      await approve(itemId, body);
      await refresh();
    } catch (error: any) {
      if (error?.error === "requires_pro") {
        openUpgradeModal("Approve HITL");
      }
    }
  };

  const handleApproveAgentTask = async (taskId: string) => {
    if (!canApproveAgentTasks) {
      openUpgradeModal("Approve Agent Tasks");
      return;
    }
    try {
      await approveTask(taskId);
      refreshTasks();
    } catch (error: any) {
      console.error("Failed to approve task:", error);
    }
  };

  const handleRejectAgentTask = async (taskId: string, reason?: string) => {
    if (!canRejectAgentTasks) {
      openUpgradeModal("Reject Agent Tasks");
      return;
    }
    try {
      await rejectTask(taskId, { reason });
      refreshTasks();
    } catch (error: any) {
      console.error("Failed to reject task:", error);
    }
  };

  const stripHtml = (html?: string | null) => {
    if (!html) return "";
    if (typeof document !== "undefined") {
      const tmp = document.createElement("div");
      tmp.innerHTML = html;
      return (tmp.textContent || tmp.innerText || "").replace(/\s+/g, " ").trim();
    }
    return html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  };

  const loadThreadMessages = async (threadId: string) => {
    if (threadMessages[threadId] || threadLoading[threadId]) return;
    setThreadLoading((prev) => ({ ...prev, [threadId]: true }));
    try {
      const messages = await listThreadMessages(threadId);
      setThreadMessages((prev) => ({ ...prev, [threadId]: messages ?? [] }));
    } catch (error) {
      push({ title: "Thread load failed", message: error instanceof Error ? error.message : "Unable to load thread." });
    } finally {
      setThreadLoading((prev) => ({ ...prev, [threadId]: false }));
    }
  };

  const getDefaultDraft = (item: HitlItem): ContactDraft => {
    const payload = (item.payload ?? {}) as Record<string, any>;
    const fromAddress = payload.from_address ?? "";
    const fromName = payload.from_name ?? fromAddress ?? "";
    return {
      name: String(fromName ?? ""),
      email: String(fromAddress ?? ""),
      city: "",
      state: "",
    };
  };

  const getContactDraft = (item: HitlItem): ContactDraft => {
    return contactEdits[item.id] ?? getDefaultDraft(item);
  };

  const updateContactDraft = (item: HitlItem, patch: Partial<ContactDraft>) => {
    setContactEdits((prev) => {
      const base = prev[item.id] ?? getDefaultDraft(item);
      return {
        ...prev,
        [item.id]: { ...base, ...patch },
      };
    });
  };

  const buildContactPayload = (item: HitlItem) => {
    const draft = getContactDraft(item);
    const normalize = (value: string) => value.trim();
    const name = normalize(draft.name);
    const email = normalize(draft.email);
    const city = normalize(draft.city);
    const state = normalize(draft.state);
    return {
      contact_name: name || undefined,
      name: name || undefined,
      contact_email: email || undefined,
      email: email || undefined,
      city: city || undefined,
      state: state || undefined,
    };
  };

  const openThreadWorkspace = async (itemId: string, threadId: string) => {
    setThreadWorkspace({ threadId, itemId });
    await loadThreadMessages(threadId);
  };

  const toggleContactSearch = (item: HitlItem) => {
    setContactSearchOpen((prev) => {
      const next = !prev[item.id];
      return { ...prev, [item.id]: next };
    });
    setContactSearchQuery((prev) => {
      if (prev[item.id]?.trim()) return prev;
      const draft = getContactDraft(item);
      const seed = draft.name?.trim() || draft.email?.trim() || "";
      return { ...prev, [item.id]: seed };
    });
  };

  const runContactSearch = async (itemId: string) => {
    const query = (contactSearchQuery[itemId] ?? "").trim();
    if (!query) {
      setContactSearchResults((prev) => ({ ...prev, [itemId]: [] }));
      setContactSearchError((prev) => ({ ...prev, [itemId]: "Enter a search term to find contacts." }));
      return;
    }
    setContactSearchLoading((prev) => ({ ...prev, [itemId]: true }));
    setContactSearchError((prev) => ({ ...prev, [itemId]: "" }));
    try {
      const payload = await fetchContacts({ query, limit: 6 });
      setContactSearchResults((prev) => ({ ...prev, [itemId]: payload.contacts ?? [] }));
      if (!payload.contacts?.length) {
        setContactSearchError((prev) => ({ ...prev, [itemId]: "No matching contacts found." }));
      }
    } catch (error) {
      setContactSearchError((prev) => ({
        ...prev,
        [itemId]: error instanceof Error ? error.message : "Unable to search contacts.",
      }));
    } finally {
      setContactSearchLoading((prev) => ({ ...prev, [itemId]: false }));
    }
  };

  const handleAddToContact = async (item: HitlItem, contact: CrmContact) => {
    const payload = buildContactPayload(item);
    if (
      payload.contact_email &&
      contact.email &&
      contact.email.toLowerCase() !== payload.contact_email.toLowerCase()
    ) {
      delete payload.contact_email;
      delete payload.email;
    }
    try {
      await handleApprove(item.id, {
        ...payload,
        contact_id: contact.id,
      });
      push({ title: "Contact updated", message: "Contact enriched and HITL item approved." });
    } catch (error) {
      push({
        title: "Add to contact failed",
        message: error instanceof Error ? error.message : "Unable to update contact.",
      });
    }
  };

  const handleOpenContactWorkspace = async (item: HitlItem) => {
    const payload = (item.payload ?? {}) as Record<string, any>;
    const contactId = payload.contact_id ?? item.metadata?.contact_id;
    if (contactId) {
      navigate(`/contacts?contact=${contactId}&hitl=${item.id}`);
      return;
    }
    try {
      const result = await approveHitlItem(item.id, buildContactPayload(item));
      const createdId = (result as { contact_id?: number })?.contact_id;
      if (createdId) {
        navigate(`/contacts?contact=${createdId}`);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Unable to open contact workspace.";
      push({ title: "Contact workspace", message });
    }
  };

  return (
    <div className="aa-page-shell space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="aa-page-title">
            <span className="aa-gradient-text">HITL Inbox</span>
          </h1>
          <p className="text-sm text-muted-foreground">Filtered snapshot of pending approvals.</p>
        </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-lg border border-border bg-background/50">
            <Button
              type="button"
              variant={viewMode === "workflow" ? "secondary" : "ghost"}
              size="sm"
              className="rounded-none px-3 text-xs font-semibold"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete("view");
                setSearchParams(params);
              }}
            >
              Workflow
            </Button>
            <Button
              type="button"
              variant={viewMode === "agent_tasks" ? "secondary" : "ghost"}
              size="sm"
              className="rounded-none px-3 text-xs font-semibold"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set("view", "agent_tasks");
                setSearchParams(params);
              }}
            >
              Agent Tasks
            </Button>
            </div>
          {viewMode === "workflow" && (
            <label className="flex items-center gap-2 text-xs uppercase text-muted-foreground">
              <input
                type="checkbox"
                checked={Boolean(filters.mineOnly)}
                onChange={(event) => handleMineToggle(event.target.checked)}
                className="h-4 w-4"
              />
              Mine only
            </label>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs"
            onClick={() => {
              if (viewMode === "agent_tasks") {
                refreshTasks();
              } else {
                refresh();
              }
            }}
          >
            Refresh
          </Button>
        </div>
      </div>

      {viewMode === "workflow" && (
        <div className="flex flex-wrap gap-2 text-sm">
          {HITL_CATEGORIES.map((tab) => (
            <Button
              key={tab.id}
              type="button"
              variant={filters.category === tab.id ? "secondary" : "outline"}
              size="sm"
              className="rounded-full px-3 text-xs"
              onClick={() => handleCategoryChange(tab.id)}
            >
              {tab.label}
            </Button>
          ))}
        </div>
      )}

      {viewMode === "workflow" && error && <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>}
      {viewMode === "agent_tasks" && tasksError && <p className="text-sm text-rose-600 dark:text-rose-300">{String(tasksError)}</p>}

      {viewMode === "workflow" && (
        <section className="aa-glass-card rounded-2xl p-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading inbox…</p>
        ) : groupedItems.length === 0 ? (
          <p className="text-sm text-muted-foreground">No pending approvals.</p>
        ) : (
          <div className="space-y-3">
            {dedupedItems.map((item) => {
              const payload = (item.payload ?? {}) as Record<string, any>;
              const isCrmEmail = item.category === "crm" && payload.from_address;
              if (!isCrmEmail) {
                return (
                  <article key={item.id} className="aa-glass-card rounded-xl p-4">
                    <div className="mb-2 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                      <span className="uppercase tracking-wide text-foreground">{item.category ?? "general"}</span>
                      <span>{formatDate(item.created_at)}</span>
                    </div>
                    <div className="text-sm font-semibold text-foreground">{item.title ?? item.node_key ?? item.agent}</div>
                    <div className="text-xs text-muted-foreground">
                      Agent: {item.agent ?? "—"} • Assigned: {item.assigned_to_display ?? item.assigned_to ?? "—"}
                    </div>
                    <div className="mt-2">
                      <HitlBadge status={item.status} deadline={item.deadline} />
                    </div>
                    {item.summary && <p className="mt-2 text-sm text-foreground/70">{item.summary}</p>}
                    <div className="mt-3 flex justify-end">
                      <CapabilityGate
                        flag="canResolveHitlItem"
                        variant="card"
                        fallback={
                          <LockedFeatureCard
                            title="Unlock HITL actions"
                            description="Upgrade to approve, reject, or inspect workflow context."
                            featureId="canResolveHitlItem"
                          />
                        }
                      >
                        <div className="flex flex-wrap gap-2">
                          <Button type="button" size="sm" variant="outline" onClick={() => openDrawer(item.id, item.thread_id)}>
                            Open Drawer
                          </Button>
                          <CapabilityGate flag="canRejectHitl" variant="hide">
                            <Button type="button" size="sm" variant="secondary" onClick={() => setRejectPrompt({ id: item.id })}>
                              Reject
                            </Button>
                          </CapabilityGate>
                          <CapabilityGate flag="canApproveHitl" variant="hide">
                            <Button type="button" size="sm" onClick={() => handleApprove(item.id)}>
                              Approve
                            </Button>
                          </CapabilityGate>
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            className="text-primary hover:text-foreground"
                            onClick={() => openInApprovals(item)}
                          >
                            View in Approvals
                          </Button>
                        </div>
                      </CapabilityGate>
                    </div>
                  </article>
                );
              }

              const subject = payload.subject ?? "No subject";
              const bodyText = stripHtml(payload.body_html ?? "") || item.summary || "";
              const threadId = payload.thread_id ?? item.thread_id;
              const draft = getContactDraft(item);

              return (
                <article
                  key={item.id}
                  className="aa-glass-card rounded-xl p-4"
                >
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span className="uppercase tracking-wide text-foreground">Contact approval</span>
                    <span>{formatDate(item.created_at)}</span>
                  </div>
                  <div className="space-y-3 text-sm text-foreground/80">
                    <div className="grid gap-3 sm:grid-cols-2">
                      <label className="space-y-1 text-xs text-muted-foreground">
                        <span className="uppercase tracking-wide">Contact name</span>
                        <Input
                          value={draft.name}
                          placeholder="Add contact name"
                          onChange={(event) => updateContactDraft(item, { name: event.target.value })}
                          className="min-h-[40px]"
                        />
                      </label>
                      <label className="space-y-1 text-xs text-muted-foreground">
                        <span className="uppercase tracking-wide">Contact email</span>
                        <Input
                          value={draft.email}
                          placeholder="email@example.com"
                          onChange={(event) => updateContactDraft(item, { email: event.target.value })}
                          className="min-h-[40px]"
                        />
                      </label>
                      <label className="space-y-1 text-xs text-muted-foreground">
                        <span className="uppercase tracking-wide">City</span>
                        <Input
                          value={draft.city}
                          placeholder="City"
                          onChange={(event) => updateContactDraft(item, { city: event.target.value })}
                          className="min-h-[40px]"
                        />
                      </label>
                      <label className="space-y-1 text-xs text-muted-foreground">
                        <span className="uppercase tracking-wide">State</span>
                        <Input
                          value={draft.state}
                          placeholder="State"
                          onChange={(event) => updateContactDraft(item, { state: event.target.value })}
                          className="min-h-[40px]"
                        />
                      </label>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-background/40 px-3 py-2 text-xs text-muted-foreground">
                      <p className="text-[11px] uppercase tracking-wide">Email summary</p>
                      <p className="text-[12px] text-foreground/80">{subject}</p>
                      <p className="text-[11px] text-foreground/60">{bodyText || "No body content."}</p>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center gap-2">
                    {threadId && (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => openThreadWorkspace(item.id, threadId)}
                      >
                        Open email thread
                      </Button>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={() => toggleContactSearch(item)}>
                      + Add to contact
                    </Button>
                    <Button type="button" size="sm" variant="ghost" onClick={() => handleOpenContactWorkspace(item)}>
                      Open contact workspace
                    </Button>
                    <CapabilityGate flag="canApproveHitl" variant="hide">
                      <Button type="button" size="sm" onClick={() => handleApprove(item.id, buildContactPayload(item))}>
                        Approve
                      </Button>
                    </CapabilityGate>
                    <CapabilityGate flag="canRejectHitl" variant="hide">
                      <Button type="button" size="sm" variant="secondary" onClick={() => setRejectPrompt({ id: item.id })}>
                        Deny
                      </Button>
                    </CapabilityGate>
                  </div>
                  {contactSearchOpen[item.id] && (
                    <div className="mt-3 rounded-xl border border-border/60 bg-background/40 p-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <Input
                          value={contactSearchQuery[item.id] ?? ""}
                          placeholder="Search contacts by name, company, or city"
                          onChange={(event) =>
                            setContactSearchQuery((prev) => ({ ...prev, [item.id]: event.target.value }))
                          }
                          className="min-h-[38px] flex-1"
                        />
                        <Button
                          type="button"
                          size="sm"
                          variant="secondary"
                          onClick={() => runContactSearch(item.id)}
                          disabled={contactSearchLoading[item.id]}
                        >
                          {contactSearchLoading[item.id] ? "Searching..." : "Search"}
                        </Button>
                      </div>
                      {contactSearchError[item.id] && (
                        <p className="mt-2 text-xs text-rose-400">{contactSearchError[item.id]}</p>
                      )}
                      {contactSearchResults[item.id]?.length ? (
                        <div className="mt-3 space-y-2">
                          {contactSearchResults[item.id].map((contact) => (
                            <div
                              key={contact.id}
                              className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border/60 bg-background/60 p-2 text-xs"
                            >
                              <div className="space-y-1">
                                <p className="text-sm font-semibold text-foreground">
                                  {contact.name || contact.company_name || `Contact ${contact.id}`}
                                </p>
                                <p className="text-[11px] text-muted-foreground">
                                  {contact.contact_name ? `${contact.contact_name} · ` : ""}
                                  {contact.location_city || "—"}
                                  {contact.location_region ? `, ${contact.location_region}` : ""}
                                </p>
                                <p className="text-[11px] text-muted-foreground">{contact.email || "No email"}</p>
                              </div>
                              <Button
                                type="button"
                                size="sm"
                                onClick={() => handleAddToContact(item, contact)}
                              >
                                Add email + name
                              </Button>
                            </div>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </section>
      )}

      {viewMode === "agent_tasks" && (
        <section className="aa-glass-card rounded-2xl p-4">
          {tasksLoading ? (
            <p className="text-sm text-muted-foreground">Loading agent tasks…</p>
          ) : !pendingTasks || pendingTasks.length === 0 ? (
            <p className="text-sm text-muted-foreground">No agent tasks pending approval.</p>
          ) : (
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <article key={task.id} className="aa-glass-card rounded-xl p-4">
                  <div className="mb-2 flex flex-wrap items-center justify-between gap-3 text-xs text-muted-foreground">
                    <span className="uppercase tracking-wide text-foreground">{task.agent} Agent</span>
                    <span>{formatDate(task.created_at)}</span>
                  </div>
                  <div className="text-sm font-semibold text-foreground">{task.title}</div>
                  <div className="text-xs text-muted-foreground">
                    Type: {getTaskTypeLabel(task.task_type)} • Status: {getTaskStatusLabel(task.status)}
                  </div>
                  {task.description && <p className="mt-2 text-sm text-foreground/70">{task.description}</p>}
                  <div className="mt-3 flex justify-end">
                    <CapabilityGate
                      flag="canApproveAgentTasks"
                      variant="card"
                      fallback={
                        <LockedFeatureCard
                          title="Unlock Agent Task Approvals"
                          description="Upgrade to approve or reject agent tasks."
                          featureId="canApproveAgentTasks"
                        />
                      }
                    >
                      <div className="flex flex-wrap gap-2">
                        <CapabilityGate flag="canRejectAgentTasks" variant="hide">
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            disabled={isSubmitting}
                            onClick={() => setRejectPrompt({ id: task.id })}
                          >
                            Reject
                          </Button>
                        </CapabilityGate>
                        <Button
                          type="button"
                          size="sm"
                          disabled={isSubmitting}
                          onClick={() => handleApproveAgentTask(task.id)}
                        >
                          Approve
                        </Button>
                      </div>
                    </CapabilityGate>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      )}

      <CapabilityGate
        flag="canOpenHitlDrawer"
        variant="card"
        fallback={selectedId ? <LockedHitlDrawer onClose={closeDrawer} feature="canOpenHitlDrawer" /> : null}
      >
        <HitlDetailDrawer
          open={Boolean(selectedId)}
          hitlId={selectedId}
          item={selectedId ? groupedItems.find((entry) => entry.id === selectedId) ?? null : null}
          onClose={closeDrawer}
          onActionComplete={() => refresh()}
          defaultCategory={filters.category ?? null}
        />
      </CapabilityGate>

      <Dialog
        open={Boolean(threadWorkspace)}
        onOpenChange={(open) => {
          if (!open) setThreadWorkspace(null);
        }}
      >
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Email thread workspace</DialogTitle>
            <DialogDescription>
              Review the full conversation before approving the contact.
            </DialogDescription>
          </DialogHeader>
          {threadWorkspace ? (
            <div className="space-y-3">
              {threadLoading[threadWorkspace.threadId] ? (
                <p className="text-sm text-muted-foreground">Loading thread…</p>
              ) : (threadMessages[threadWorkspace.threadId] ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">No thread history.</p>
              ) : (
                (threadMessages[threadWorkspace.threadId] ?? []).map((message: any) => (
                  <div key={message.id} className="rounded-xl border border-border bg-background/50 p-3 text-sm">
                    <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground">
                      <span className="uppercase tracking-wide">
                        {message.direction === "inbound" ? "Inbound" : "Outbound"}
                      </span>
                      <span>{formatDate(message.sent_at || message.created_at)}</span>
                    </div>
                    <p className="mt-2 text-sm font-semibold text-foreground">
                      {message.subject || "(No subject)"}
                    </p>
                    <p className="mt-1 text-xs text-foreground/70">
                      {stripHtml(message.body_html) || "No content."}
                    </p>
                    <p className="mt-2 text-[11px] text-muted-foreground">
                      {message.from_address || "Unknown sender"} → {message.to_address || "Unknown recipient"}
                    </p>
                  </div>
                ))
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {rejectPrompt && (
        <HitlActionPrompt
          title={viewMode === "agent_tasks" ? "Reject agent task" : "Reject HITL item"}
          placeholder="Share why this cannot proceed."
          confirmLabel="Reject"
          value={rejectNotes}
          onChange={setRejectNotes}
          onCancel={() => {
            setRejectPrompt(null);
            setRejectNotes("");
          }}
          onConfirm={() => {
            if (viewMode === "agent_tasks") {
              handleRejectAgentTask(rejectPrompt.id, rejectNotes.trim());
              setRejectPrompt(null);
              setRejectNotes("");
            } else {
              handleRejectPrompt();
            }
          }}
        />
      )}
    </div>
  );
}
